/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Sparkles, MessageSquare, Calendar, TrendingUp } from 'lucide-react';

const AIStatsCard = ({ endpoint = '/ai/stats', titleKey = 'aiStatsTitle', showTopUsers = false }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try { const res = await api.get(endpoint); setStats(res.data); }
      catch {}
      finally { setLoading(false); }
    };
    load();
  }, [endpoint]);

  if (loading) return <div className="text-sm text-muted-foreground">{t('loading')}</div>;
  if (!stats || stats.total_messages === 0) return null;

  const items = showTopUsers ? [
    { Icon: MessageSquare, label: t('aiStatTotalExchanges'), value: stats.total_messages, color: 'var(--wl-primary)' },
    { Icon: Sparkles, label: t('aiStatSessions'), value: stats.total_sessions, color: '#DB2777' },
    { Icon: Calendar, label: t('aiAdminActiveUsers'), value: stats.active_users, color: '#1D4ED8' },
    { Icon: TrendingUp, label: t('aiStatMyMessages'), value: stats.user_messages, color: '#7C3AED' },
  ] : [
    { Icon: MessageSquare, label: t('aiStatMyMessages'), value: stats.user_messages, color: 'var(--wl-primary)' },
    { Icon: Sparkles, label: t('aiStatSessions'), value: stats.total_sessions, color: '#DB2777' },
    { Icon: Calendar, label: t('aiStatActiveDays'), value: stats.active_days_30d, color: '#1D4ED8' },
    { Icon: TrendingUp, label: t('aiStatTotalExchanges'), value: stats.total_messages, color: '#7C3AED' },
  ];

  // Sparkline
  const maxDaily = Math.max(1, ...(stats.daily_30d || []).map(d => d.count));

  return (
    <Card className="p-4 rounded-2xl bg-card border border-border" data-testid="ai-stats-card">
      <div className="font-display font-semibold text-sm mb-3 flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" /> {t(titleKey)}
      </div>
      <div className="grid grid-cols-4 gap-2">
        {items.map((it, idx) => (
          <div key={it.label || `stat-${idx}`} className="p-2 rounded-xl bg-secondary/50 text-center" data-testid={`ai-stat-item-${idx}`}>
            <div className="h-7 w-7 rounded-lg flex items-center justify-center mx-auto" style={{ backgroundColor: `${it.color}18`, color: it.color }}>
              <it.Icon className="h-4 w-4" />
            </div>
            <div className="text-lg font-display font-semibold mt-1">{it.value}</div>
            <div className="text-[10px] text-muted-foreground leading-tight">{it.label}</div>
          </div>
        ))}
      </div>
      {stats.daily_30d && stats.daily_30d.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] text-muted-foreground mb-1">{t('aiStatLast30d')}</div>
          <div className="flex items-end gap-0.5 h-16">
            {stats.daily_30d.map((d) => (
              <div key={d.date} className="flex-1 min-w-[3px] rounded-t" style={{ height: `${(d.count / maxDaily) * 100}%`, backgroundColor: 'hsl(var(--primary))', opacity: 0.75 }} title={`${d.date}: ${d.count}`} />
            ))}
          </div>
        </div>
      )}
      {showTopUsers && stats.top_users && stats.top_users.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <div className="text-[11px] text-muted-foreground mb-2 uppercase tracking-wider">{t('aiAdminTopUsers')}</div>
          <div className="space-y-1.5">
            {stats.top_users.slice(0, 5).map((tu, i) => (
              <div key={tu.user?.id || tu.user?.email || `top-${i}`} className="flex items-center justify-between text-sm" data-testid={`ai-admin-top-user-${i}`}>
                <div className="truncate flex-1 min-w-0">
                  <span className="text-muted-foreground text-xs mr-2">#{i + 1}</span>
                  {tu.user?.full_name || '—'}
                </div>
                <div className="text-xs font-semibold text-primary">{tu.message_count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default AIStatsCard;
